import { AddOutlined } from '@mui/icons-material'
import { Box, Button, Drawer, Stack, TableContainer } from '@mui/material'
import type { PlaceNode } from '@ulysses-inc/harami_api_client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { useMutationDeletePlaceGroup } from 'src/features/places/api/useMutationDeletePlaceGroup'
import { useQueryPlaceGroups } from 'src/features/places/api/useQueryPlaceGroups'
import EditPlaceGroup, {
  flattenNodes,
} from 'src/features/places/EditPlaceGroup'
import type { FlatNode } from 'src/features/places/PlaceGroupsProps'
import placesOperations from '../../state/ducks/places/operations'
import PlaceGroupList from './PlaceGroupList'

interface StateProps {
  nodes: PlaceNode[]
  flatNodes: FlatNode[]
  isLoading: boolean
  isShowEditPlaceGroupDrawer: boolean
}

interface DispatchProps {
  openAddPlaceGroup: (parentNodeId?: number) => void
  openEditPlaceGroup: (node: PlaceNode, parentNodeId?: number) => void
  deletePlaceGroup: (id: string) => void
}

export type PlaceGroupsProps = StateProps & DispatchProps

const PlaceGroupsScene = () => {
  const dispatch = useDispatch()

  const { data: nodes = [], isLoading } = useQueryPlaceGroups()
  const flatNodes = useMemo(() => flattenNodes(nodes, []), [nodes])

  const [isShowEditPlaceGroupDrawer, setIsShowEditPlaceGroupDrawer] =
    useState(false)
  const [activeNodeId, setActiveNodeId] = useState<string | undefined>()
  const [activeParentNodeId, setActiveParentNodeId] = useState<
    number | undefined
  >()

  const deleteMutation = useMutationDeletePlaceGroup()

  useEffect(() => {
    placesOperations.getPlaces(dispatch, {})
  }, [dispatch])

  const onClose = useCallback(() => {
    placesOperations.updateSegmentIndex(dispatch, 0)
    setIsShowEditPlaceGroupDrawer(false)
  }, [dispatch])

  const listProps: PlaceGroupsProps = {
    nodes,
    flatNodes,
    isLoading,
    isShowEditPlaceGroupDrawer,
    openAddPlaceGroup: (parentNodeId?: number) => {
      setActiveNodeId(undefined)
      setActiveParentNodeId(parentNodeId)
      setIsShowEditPlaceGroupDrawer(true)
    },
    openEditPlaceGroup: (node: PlaceNode, parentNodeId?: number) => {
      setActiveNodeId(node.uuid)
      setActiveParentNodeId(parentNodeId)
      setIsShowEditPlaceGroupDrawer(true)
    },
    deletePlaceGroup: (nodeId: string) => {
      deleteMutation.mutate({ placeNodeId: nodeId })
    },
  }

  return (
    <Stack
      sx={{
        height: 'calc(100vh - var(--banner-height, 0px))',
      }}
    >
      <Box
        sx={{
          height: 72,
          width: '100%',
          alignItems: 'center',
          boxSizing: 'border-box',
          display: 'block',
          flexDirection: 'row',
        }}
      >
        <Header>
          <HeaderTitle title="現場グループ" />
        </Header>
      </Box>
      <Box
        width="100%"
        display="flex"
        justifyContent="space-between"
        sx={{ py: 2, px: 3 }}
      >
        <Box />
        <Button
          color="primary"
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={() => listProps.openAddPlaceGroup()}
        >
          現場グループを追加
        </Button>
      </Box>
      <TableContainer
        sx={{
          overflow: 'auto',
          position: 'relative',
          flexGrow: 1,
          bgcolor: 'white',
        }}
      >
        <PlaceGroupList {...listProps} />
      </TableContainer>
      <Drawer
        anchor="right"
        open={isShowEditPlaceGroupDrawer}
        onClose={() => onClose()}
        hideBackdrop={false}
        ModalProps={{
          keepMounted: false,
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '100%',
              width: '100%',
              maxWidth: 600,
            },
          },
        }}
      >
        <EditPlaceGroup
          nodes={nodes}
          activeNodeId={activeNodeId}
          activeParentNodeId={activeParentNodeId}
          onClose={onClose}
        />
      </Drawer>
    </Stack>
  )
}

export default PlaceGroupsScene
